<script setup lang="ts">
import { computed, defineEmits, inject, Ref, ref } from 'vue'
import * as apisPortal from '/@src/client-portal/api'
import { useFormValues } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { useNotyf } from '/@src/composable/useNotyf'
import FileDownload from 'js-file-download'
import { useZoppm } from '/@src/composable/useZoppm'
import { Dokument } from '/@src/client-portal'
import { MAX_FILE_SIZE_MB } from '/@src/constants/index'
import { checkFiles } from '/@src/utils/checkFiles'

type FilesListType = ReadonlyArray<{
  key: string
  value: string
  id: string
}>

export interface VydavokProjektuPrilohy {
  arrayPosition: number
  polozkaIndex: number
  filesList: FilesListType
  name: string
}

const props = defineProps<VydavokProjektuPrilohy>()
const { t } = useI18n()
const notif = useNotyf()

const api = new apisPortal.ZoppmDraftApi()
const kodIrz = inject<Ref<string>>('kodIrz')
const isReadOnly = inject<boolean>('isReadOnly')
const zoppm = !isReadOnly ? useZoppm() : null
const formValues = useFormValues()
const loadingFile = ref<boolean>(false)

export interface VydavokProjektPrilohyEmits {
  (e: 'add', value: Required<Dokument>): void
  (e: 'remove', value: string): void
}

const emit = defineEmits<VydavokProjektPrilohyEmits>()

const dokumenty = computed(
  () =>
    formValues?.value?.projekt?.rozpocet?.polozky?.[props.polozkaIndex]?.prilohy?.[
      props.arrayPosition
    ]?.priloha?.dokumenty
)

async function uploadFile(file: File[]) {
  loadingFile.value = true
  const checkedFiles = checkFiles(file)
  const promises: any[] = []

  // Pouzivatel moze nahrat aj viacero suborov, tak to prebehneme
  if (kodIrz?.value) {
    try {
      Array.from(checkedFiles).forEach((checkedFiles) => {
        promises.push(
          api.storeDokumentForZoppmDraftZoppmDraft(kodIrz.value, checkedFiles)
        )
      })

      await Promise.all(promises)
        .then((responses) => {
          responses.forEach(({ data }) => {
            notif.success(`Súbor ${data?.nazov}.${data?.format} bol úspešne nahraný`)

            emit('add', {
              nazov: data?.nazov,
              format: data?.format,
              dokumentRef: data?.ref,
            })
          })
        })
        .catch((error) => {
          if (error?.response?.data?.exception) {
            notif.error(error.response.data.exception)
          }
        })

      // Nasledne updatujeme aj Draft pretoze ak by pouzivatel refreshol, tak subor ostane nahrany na serveri
      // ale on by ho uz nevidel
      await zoppm?.handleUpdateDraft(kodIrz.value, formValues.value)
    } catch (error) {
      notif.error('Súbor sa nepodarilo nahrať, chyba siete.')
      loadingFile.value = false
    }
  }
  loadingFile.value = false
}

async function handleFileRemove(identifikatorUlozisko: string, index: number) {
  // TODO: na remove, odstranit subor aj z VeeValidate a zavolat `handleUpdateDraft`
  if (kodIrz?.value) {
    // const verziaUlozisko = dokumenty.value?.find(
    //   (file) => file?.dokumentRef?.identifikatorUlozisko === identifikatorUlozisko
    // )?.dokumentRef?.verziaUlozisko
    emit('remove', identifikatorUlozisko)
  }
}

async function getDokument(identifikatorUlozisko: string) {
  const verziaUlozisko = dokumenty.value?.find(
    (file) => file?.dokumentRef?.identifikatorUlozisko === identifikatorUlozisko
  )?.dokumentRef?.verziaUlozisko

  if (kodIrz?.value) {
    try {
      await api
        .fetchContentOfDokumentForZoppmDraftZoppmDraft(
          kodIrz.value,
          identifikatorUlozisko,
          verziaUlozisko,
          undefined,
          undefined,
          {
            responseType: 'blob',
          }
        )
        .then((response) => {
          FileDownload(response.data, response.headers.filename)
        })
    } catch {}
  }
}
</script>

<template>
  <IDSKSummaryList
    v-if="!isReadOnly"
    :data="filesList"
    action-text="Odstrániť"
    @action="handleFileRemove"
  >
    <template #link="{ value }">
      <IDSKLink href="#" no-visited-state @click.prevent="() => getDokument(value.id)">
        {{ value.key }}
      </IDSKLink>
    </template>
  </IDSKSummaryList>

  <loading-spinner v-if="loadingFile" />

  <IDSKDragAndDrop
    :show-defaul-summary-list="!!isReadOnly"
    :label="t('zoppm.step13.fileUploadLabel')"
    :hint="t('zoppm.step13.fileUploadHint') + ` (max. ${MAX_FILE_SIZE_MB}MB)`"
    @added-files="(files) => !isReadOnly && uploadFile(files)"
  />
</template>
